import { Box, Text, Title } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Download } from '@factorialco/f0-react/icons/app'
import { BenefitsPlanDetailsFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

interface CostBreakdownItemProps {
  label: string
  totalCostCents: number | null | undefined
  coveredCostCents: number | null | undefined
  locale: string
}

const CostBreakdownItem = ({
  label,
  totalCostCents,
  coveredCostCents,
  locale,
}: CostBreakdownItemProps) => {
  const totalAmount = totalCostCents ?? 0
  const coveredAmount = coveredCostCents ?? 0
  const employeeAmount = totalAmount - coveredAmount

  const formatMoney = (amount: number | null | undefined) => {
    if (amount == null) return '-'
    return MoneyWithCurrency({
      amount,
      locale,
      currency: 'EUR',
    })
  }

  return (
    <Box gap="s4" flexDirection="column">
      <Text size="200">{`${label}: ${formatMoney(totalCostCents)}`}</Text>
      <Text size="200" color="grey600">
        {`${i18n.t('benefits.healthcare.management.company_pays')}: ${formatMoney(coveredCostCents)}
        • ${i18n.t('benefits.healthcare.management.employee_pays')}: ${formatMoney(
          employeeAmount
        )}`}
      </Text>
    </Box>
  )
}

interface PlanDetailsProps {
  plan: BenefitsPlanDetailsFragment
}

export const PlanDetails = ({ plan }: PlanDetailsProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const planDocumentUrl = plan.planDocument?.url
  return (
    <Box gap="s8">
      <Title variant="300" title={i18n.t('benefits.healthcare.management.plan_details')} />
      <Box padding="s16" background="grey100" gap="s12" flexDirection="column">
        <Text size="200">
          <Text size="200" weight="bold">
            {i18n.t('benefits.healthcare.management.provider')}:
          </Text>
          {` ${plan.providerName}`}
        </Text>
        <Text size="200">
          <Text size="200" weight="bold">
            {i18n.t('benefits.healthcare.management.plan_name')}:
          </Text>
          {` ${plan.planName}`}
        </Text>

        {/* Dependents Allowed */}
        <Text size="200">
          <Text size="200" weight="bold">
            {i18n.t('benefits.healthcare.management.dependents_allowed')}:
          </Text>
          {` ${
            plan.dependentCostCents != null
              ? i18n.t('benefits.healthcare.management.yes')
              : i18n.t('benefits.healthcare.management.no')
          }`}
        </Text>

        {/* Cost Breakdown */}
        <Box gap="s8" flexDirection="column">
          <Text size="200" weight="bold">
            {i18n.t('benefits.healthcare.management.cost_breakdown')}:
          </Text>
          <CostBreakdownItem
            label={i18n.t('benefits.healthcare.management.employee_premium')}
            totalCostCents={plan.employeeCostCents}
            coveredCostCents={plan.coveredEmployeeCostCents}
            locale={locale}
          />
          {plan.dependentCostCents != null && plan.coveredDependentCostCents != null && (
            <CostBreakdownItem
              label={i18n.t('benefits.healthcare.management.dependent_premium')}
              totalCostCents={plan.dependentCostCents}
              coveredCostCents={plan.coveredDependentCostCents}
              locale={locale}
            />
          )}
        </Box>

        {planDocumentUrl && (
          <Box alignSelf="flexStart">
            <F0Button
              label={i18n.t('benefits.actions.download_plan')}
              variant="outline"
              href={planDocumentUrl}
              target="_blank"
              icon={Download}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
